import { format } from 'date-fns';
import iziToast from 'izitoast';
import { pathImage } from 'src/components/shared/common';
import { FileProps } from 'src/components/shared/DragAndDrop';
import { getAgentData, translatePromptService } from 'src/services/AgentService';
import { Source } from 'src/types/Source';
import { VectorStore } from 'src/types/VectorStore';
import { getFilesService, getFolderFilesService, uploadFileService } from '../services/AssetService';
import { State } from '../store';
import { stringFormat } from './botGenerator/formatScript';
import * as XLSX from 'xlsx';
import { Permissions, PermissionsData, PermissionType, UserPermissions } from 'src/types/Permission';
import { Country } from 'src/types/Session';
import { formatInitBot } from './botGenerator/formatScript2';
import { formatCss } from './botGenerator/formatCss';
import { ExtraConfig, ExtraConfigKeys, InfoWatch } from 'src/types/AiAgentV3';

export const downloadFile = (text: string, filename: string, type: string) => {
    const a = window.document.createElement('a');
    a.setAttribute('id', filename);
    a.setAttribute('href', `data:text/${type}; charset=utf-8,` + encodeURIComponent(text));
    a.setAttribute('download', `${filename}.${type}`);
    a.click();
};

// get only first letter in uppercase
export function capitalize(str: string) {
    if (str == '') {
        return str;
    }
    return str.replace(str[0], str[0].toUpperCase());
}

//get data from json with key
export function getDataFromKey<T>(data: T, key: string): string {
    try {
        let val: any = data;
        key.split('.').forEach((key) => {
            // console.log(key, val);
            val = val[key];
        });
        return val as string;
    } catch (e) {
        console.log(e);
        return '';
    }
}

export function uuidV4(): string {
    return `${makeId()}-${makeId()}-${makeId()}-${makeId()}`;
}

export function makeId() {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < 5; i++) text += possible.charAt(Math.floor(Math.random() * possible.length));

    return text;
}

// validate if a string is a valid JSON
export function isJsonString(str: string) {
    try {
        JSON.parse(str);
        return true;
    } catch (e) {
        return false;
    }
}

// Email is valid with regex expression
export function isEmail(email: string) {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
}

export function iterator(f: number, u: number) {
    const arr: number[] = [];
    let i = f;
    while (i <= u) arr.push(i++);
    return arr;
}

export function toMap<T>(array: T[], key: string, only?: string) {
    return array.reduce((obj: Record<string, T>, row: T) => {
        const _keyValue = getDataFromKey(row, key);
        if (only) {
            obj[_keyValue] = getDataFromKey(row, only) as T;
        } else {
            obj[_keyValue] = row;
        }
        return obj;
    }, {});
}

export function getQueryParams(url: string) {
    const queryString = url.split('?')[1];
    if (!queryString) return {};
    return queryString.split('&').reduce((acc: Record<string, string>, item) => {
        const [key, value] = item.split('=');
        acc[key] = value;
        return acc;
    }, {});
}

export class DateUtil {
    private date: Date;
    private format: string;

    constructor(date: Date, format = 'yyyy-MM-dd') {
        this.date = date;
        this.format = format;
    }

    public getDate() {
        return format(this.date, this.format);
    }
}

export const formatField = (word_str: string) => {
    return word_str
        .replaceAll('_', ' ')
        .toLowerCase()
        .split(' ')
        .map((word) => word.replace(/[^a-z0-9]/gi, ''))
        .join(' ')
        .replace(/ /g, '_');
};

export function formatSizeUnits(bytes: number) {
    let byteText: string;
    if (bytes >= 1073741824) {
        byteText = (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
        byteText = (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
        byteText = (bytes / 1024).toFixed(2) + ' KB';
    } else if (bytes > 1) {
        byteText = bytes + ' bytes';
    } else if (bytes == 1) {
        byteText = bytes + ' byte';
    } else {
        byteText = '0 bytes';
    }
    return byteText;
}

export async function getFileInfo(url: string): Promise<FileProps> {
    try {
        const response = await fetch(url);

        if (!response.ok) {
            iziToast.warning({
                message: 'This is not a valid file',
                position: 'topCenter',
            });
            return {
                name: '',
                type: '',
                size: 0,
            };
        }

        const blob = await response.blob();

        const fileName = url.split('/').pop() || 'archivo_desconocido';
        const file = new File([blob], fileName, { type: blob.type });

        return {
            name: file.name,
            type: file.type,
            size: file.size,
        };
    } catch (error) {
        console.error(error);
        return {
            name: '',
            size: 0,
            type: '',
        };
    }
}

export async function urltoFileProps(urls: string[], fileTypes: string[]): Promise<FileProps[]> {
    const types = getFilesTypes(fileTypes);
    return await Promise.all(
        urls.map(async (valueUrl) => {
            let file: FileProps | null;
            if (valueUrl.includes('youtube')) {
                return {
                    name: valueUrl,
                    size: 10000,
                    type: 'youtube',
                };
            } else {
                file = await getFileInfo(valueUrl);
            }

            if (file) {
                if (types.some((type) => (file as FileProps).type.includes(type))) {
                    return file;
                }
            }
            return {
                name: '',
                size: 0,
                type: '',
            };
        })
    );
}

function getFilesTypes(fileTypes: string[]) {
    return fileTypes.reduce((acc: string[], val) => {
        val = val.toLowerCase();
        if (val.includes('pdf')) {
            acc.push('pdf');
        } else if (val.includes('docx')) {
            acc.push('wordprocess');
        } else if (val.includes('audio')) {
            acc.push('mpeg');
        } else if (val.includes('xlsx')) {
            acc.push('spreadsheet');
        } else if (val.includes('csv')) {
            acc.push('csv');
        } else if (val.includes('text')) {
            acc.push('text');
        } else if (val.includes('video')) {
            acc.push('mp4');
        }

        return acc;
    }, []);
}

export function tryToJsonParse(data: string) {
    try {
        return JSON.parse(data);
    } catch (e) {
        return [];
    }
}

export function typeFileComponent(files: FileProps[], items: any[]) {
    return files.map((filesC) => {
        const item = items.find((ite) => ite.name === filesC.name);
        if (item) {
            return {
                key: item.key,
                name: item.name,
                size: filesC.size,
                url: filesC.url,
                type: filesC.type,
            };
        }

        return filesC;
    });
}

export const convertDateToYYYYMMDDHHmm = (date: Date, formatDate = 'yyyy-MM-dd HH:mm') => {
    return format(date, formatDate);
};

export function compareArrayKnowledge(arr1: FileProps[] | string[], arr2: FileProps[] | string[]) {
    function getName(item: FileProps | string): string {
        if (typeof item === 'string') {
            return item;
        }
        return item.name;
    }

    const Array1 = arr1.map(getName);
    const Array2 = arr2.map(getName);
    const compareArray1 = Array1.some((name) => !Array2.includes(name));
    const compareArray2 = Array2.some((name) => !Array1.includes(name));

    return compareArray1 || compareArray2;
}

export function convertTextToFile(text: string, type: 'js' | 'json' | 'txt' | 'css', name: string): File {
    const blob = new Blob([text], { type: 'text/' + type });
    return new File([blob], `${name}.${type}`);
}

export async function generateJsBotFile(
    agentId: string | number,
    name: string,
    key: string,
    assessorId: string | number,
    domainImages: string,
    withWhatsApp: boolean,
    withMicrophone: boolean,
    withAssessor: boolean,
    withAttachment: { pdf: boolean; image: boolean; excel: boolean; text: boolean; word: boolean },
    withInitialMessage: boolean,
    countries: Country[],
    defaultCountry: string,
    stateCb: (state: State) => void,
    version: string
) {
    stateCb(State.LOADING);
    //get format text from file formatScript.js

    const text = stringFormat({
        baseUrl: process.env.REACT_APP_BASE_URL || window.location.origin,
        agentId,
        assessorId,
        domainUrl: domainImages,
        withWhatsApp,
        withMicrophone,
        withAssessor,
        withAttachment,
        previewMode: false,
        withInitialMessage,
        version,
        defaultCountry,
        countries,
    });

    if (!localStorage.getItem('SCRIPT_TEST')) {
        const file = convertTextToFile(text, 'js', name);
        const resUpload = await uploadFileService(key, [file]);
        if (resUpload.success) {
            stateCb(State.SUCCESS);
        } else {
            stateCb(State.ERROR);
        }
    } else {
        stateCb(State.SUCCESS);
        console.log(text);
    }
}

export async function generateJsBotFileV3(
    urlFiles: string,
    agentId: string,
    keyCssBot: string,
    keyJsBot: string,
    withMicrophone: boolean,
    withAssessor: boolean,
    withAttachment: { pdf: boolean; image: boolean; excel: boolean; text: boolean; word: boolean },
    withInitialMessage: boolean,
    stateCb: (state: State) => void
) {
    stateCb(State.LOADING);
    //get format text from file formatScript.js
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    const dataBot = await getAgentData(baseUrl, agentId);
    const name = dataBot.name + 'v3';
    const css = formatCss(dataBot.look_and_feel_chat?.extra_config, dataBot.id);
    const cssFile = convertTextToFile(css, 'css', name);
    uploadFileService(keyCssBot, [cssFile]).then();
    const cssUrl = urlFiles.concat('css/', name + '.css');
    const text = formatInitBot({
        baseUrl,
        agentId,
        withMicrophone,
        withAssessor,
        withAttachment,
        withInitialMessage,
        dataBot,
        urlCss: cssUrl,
        urlFiles,
        policy_terms_url: dataBot?.look_and_feel_chat?.privacy_policy_url ?? '',
    });

    if (!localStorage.getItem('SCRIPT_TEST')) {
        const file = convertTextToFile(text, 'js', name);
        const resUpload = await uploadFileService(keyJsBot, [file]);
        if (resUpload.success) {
            stateCb(State.SUCCESS);
        } else {
            stateCb(State.ERROR);
        }
    } else {
        stateCb(State.SUCCESS);
        console.log(text);
    }
}

export const imgVectorStore = (text: string) => {
    const oracle = pathImage + '/oracle_db_ai_23.png';
    const heatwave = pathImage + '/oracle_mysql.png';
    // const pinecone = pathImage + '/pinecone_logo.jpg';

    if (text.toLowerCase() === VectorStore.OracleDb23Ai.toLowerCase()) return oracle;
    else if (text.toLowerCase() === VectorStore.Heatwave.toLowerCase()) return heatwave;
    else if (text.toLowerCase() === VectorStore.Pinecone.toLowerCase()) return null;

    return null;
};

export const imgSource = (text: string) => {
    const cohere = [`${pathImage}/og_generative_ai.avif`, `${pathImage}/cohere_1.webp`];
    const llama2 = [`${pathImage}/og_generative_ai.avif`, `${pathImage}/llama2.jpeg`];
    const HeatwaveMistral = [`${pathImage}/oracle_mysql.png`, `${pathImage}/mistral.svg`];
    const HeatwaveLlama2 = [`${pathImage}/oracle_mysql.png`, `${pathImage}/llama2.jpeg`];

    if (text.toLowerCase() === Source.Cohere.toLowerCase()) return cohere;
    else if (text.toLowerCase() === Source.LLama2.toLowerCase()) return llama2;
    else if (text.toLowerCase() === Source.HeatwaveMistral.toLowerCase()) return HeatwaveMistral;
    else if (text.toLowerCase() === Source.HeatwaveLlAMA2.toLowerCase()) return HeatwaveLlama2;

    return null;
};

export const mergeObjects = (obj1: any, obj2: any) => {
    return Object.keys({ ...obj1, ...obj2 }).reduce((acc: any, key: any) => {
        if (obj1[key] && obj2[key] && typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
            acc[key] = mergeObjects(obj1[key], obj2[key]);
        } else {
            acc[key] = obj2[key] !== undefined ? obj2[key] : obj1[key];
        }
        return acc;
    }, {});
};

export function randomColorHex() {
    const letras = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letras[Math.floor(Math.random() * 16)];
    }
    return color;
}

export const copyToClipboard = (text: string, message = 'Texto copiado') => {
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);
    textarea.value = text;
    textarea.select();
    textarea.setSelectionRange(0, 99999);
    document.execCommand('copy');
    document.body.removeChild(textarea);
    iziToast.success({ position: 'topCenter', message });
};

export function containsNumbers(str: string) {
    return /[0-9]/.test(str);
}

export async function addFilesByFolder(data: { [key: string]: any }) {
    data = { ...data };
    try {
        // Verificamos si es un objeto
        if (typeof data === 'object' && data !== null) {
            for (const key in data) {
                // Si encontramos la clave 'folder'
                if (key === 'folder' && data[key]) {
                    const response = await getFolderFilesService(data[key]);
                    if (response?.success && response.items) {
                        const items = response.items.map((item) => ({
                            key: item.key,
                            name: item.name,
                            type: item.mimetype,
                            size: item.size,
                        }));

                        data['files'] = [];
                        data['filesComponent'] = items ?? [];
                    }
                }

                // Si encontramos la clave 'file'
                if ((key === 'file' || key === 'source_file') && data[key]) {
                    const response = await getFilesService(data[key]);
                    if (response?.success && response.item) {
                        const newFile = await fetch(response.item.url);
                        const blobFile = await newFile.blob();
                        const item = new File([blobFile], response.item.name, { type: response.item.mime_type });

                        data['files'] = [item];
                        data['filesComponent'] = [item];
                    }
                }
                // Si el valor es otro objeto, hacemos una llamada recursiva y asignamos a la key
                else if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
                    if (data[key]) {
                        data[key] = await addFilesByFolder(data[key]);
                    }
                }
            }
        }
        return data;
    } catch (error) {
        console.error(error);
        return data;
    }
}

export function extractFolderKeys(data: { [key: string]: any }): string[] {
    const folderKeys: string[] = [];

    function recursiveSearch(obj: { [key: string]: any }) {
        if (typeof obj === 'object' && obj !== null) {
            for (const key in obj) {
                if (key === 'folder' || key === 'file' || key === 'source_file') {
                    const folderValue = obj[key];
                    // Solo agregar valores que no sean null, undefined o cadenas vacías
                    if (folderValue !== null && folderValue !== undefined && folderValue !== '') {
                        folderKeys.push(folderValue);
                    }
                } else if (typeof obj[key] === 'object') {
                    recursiveSearch(obj[key]); // Llamada recursiva si es otro objeto
                }
            }
        }
    }

    recursiveSearch(data);
    return folderKeys;
}

export const validatePermissionModule = ({
    permissions_list,
    module,
    types,
}: {
    permissions_list: UserPermissions[];
    module: Permissions;
    types: PermissionType[];
}) => {
    if (!permissions_list) {
        return [];
    }

    const modulePermissions = permissions_list
        .filter((permission) => permission.module_name === module)
        .reduce(
            (
                obj: {
                    [key: string]: UserPermissions;
                },
                permission
            ) => {
                obj[permission.permission] = permission;
                return obj;
            },
            {}
        );

    return types.reduce((arr: (string | number)[], type) => {
        const typeKeys =
            modulePermissions[type]?.metadata && Array.isArray(modulePermissions[type].metadata)
                ? modulePermissions[type].metadata.reduce((arrType: string[], meta) => {
                      if (
                          isJsonString(meta) &&
                          JSON.parse(meta).allow &&
                          JSON.parse(meta).allow.every(
                              (item: string | number) => typeof item === 'string' || typeof item === 'number'
                          )
                      ) {
                          const allow = JSON.parse(meta).allow;
                          if (allow.includes('all')) {
                              arrType.push('all');
                          } else {
                              arrType = arrType.concat(allow);
                          }
                      }

                      if (arrType.includes('all')) {
                          arrType = [];
                      }

                      return arrType;
                  }, [])
                : [];

        arr = arr.concat(typeKeys);

        return arr;
    }, []);
};

export async function validateAndTranslate<DataType, OldValueType>(
    data: DataType,
    oldValues: OldValueType,
    submittedData: DataType,
    fields: { key: keyof DataType; keyEn: keyof DataType }[],
    setProgressBar?: React.Dispatch<React.SetStateAction<number | null>>,
    skipOldValuesCheck?: boolean
) {
    const missingFields = fields.filter(({ keyEn }) => !data[keyEn]);

    let changedFields = fields.filter(
        ({ key, keyEn }) => data[key] !== (oldValues as any)[key] && data[keyEn] === (oldValues as any)[keyEn]
    );

    // If skipOldValuesCheck is true, ignore the validation with oldValues
    if (skipOldValuesCheck) {
        changedFields = fields.filter(({ key }) => data[key]);
    }

    if (missingFields.length === fields.length || changedFields.length === fields.length) {
        // All fields are either missing or changed
        const promises = changedFields
            .filter(({ key }) => data[key]) // Verify that the field contains data.
            .map(({ key }) => translatePromptService((data as any)[key]));
        const responses = await Promise.all(promises);

        if (responses.every((response) => response.success)) {
            responses.forEach((response, index) => {
                const { keyEn } = changedFields[index];
                (submittedData as any)[keyEn] = response.item;
            });
            setProgressBar?.(1);
        }
    } else {
        // Handle individual changed fields
        for (const { key, keyEn } of changedFields) {
            if ((data as any)[key]) {
                const response = await translatePromptService((data as any)[key]);
                if (response.success) {
                    (submittedData as any)[keyEn] = response.item;
                    setProgressBar?.(1);
                }
            }
        }
    }
}

export async function validateCSVDelimiter(file: File): Promise<',' | ';' | 'Unknown'> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            const content = event.target?.result as string;

            if (!content) {
                reject('The file is empty or could not be read.');
                return;
            }

            // Extract the first few lines of the file
            const lines = content.split('\n').slice(0, 5); // Analyze up to 5 lines
            let commas = 0;
            let semicolons = 0;

            for (const line of lines) {
                commas += (line.match(/,/g) || []).length;
                semicolons += (line.match(/;/g) || []).length;
            }

            // Compare the occurrences
            if (commas > semicolons) {
                resolve(',');
            } else if (semicolons > commas) {
                resolve(';');
            } else {
                resolve('Unknown');
            }
        };

        reader.onerror = () => {
            reject('An error occurred while reading the file.');
        };

        reader.readAsText(file); // Read the file as text
    });
}

export async function readCsvFile(file: File): Promise<Record<string, any>[]> {
    return new Promise(async (resolve, reject) => {
        const delimiter = await validateCSVDelimiter(file);
        if (delimiter === ',' || delimiter === ';') {
            const reader = new FileReader();
            reader.onload = (e: ProgressEvent<FileReader>) => {
                const text = new TextDecoder('iso-8859-1').decode(e.target?.result as ArrayBuffer);
                const rows = text.split('\n').map((row) => row.split(delimiter).map((cell) => cell.trim()));
                const headers = rows[0];
                const jsonData = rows
                    .slice(1)
                    .filter((row) => row.length > 1)
                    .map((row) => {
                        const obj: { [key: string]: string } = {};
                        row.forEach((cell, index) => {
                            obj[headers[index]] = cell;
                        });
                        return obj;
                    });
                resolve(jsonData);
            };
            reader.onerror = () => {
                reject(false);
            };
            reader.readAsArrayBuffer(file); // We use ArrayBuffer to handle special characters
        } else {
            reject(false);
        }
    });
}

export async function readExcelFile(file: File): Promise<Record<string, any>[]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            const data = new Uint8Array(event.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });

            // Assume the first sheet is the one we want
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];

            // Convert the sheet to JSON
            const jsonData: Record<string, any>[] = XLSX.utils.sheet_to_json(sheet);

            resolve(jsonData);
        };

        reader.onerror = () => {
            reject(false);
        };

        reader.readAsArrayBuffer(file); // Read the file as binary data
    });
}

//TODO: After demo remove this function and use the real name of the skill
export const temporallyLabelOpenAiToOracle = (label?: string) => {
    switch (label?.toLowerCase()) {
        case 'openai':
            return 'Oracle';

        default:
            return label || '';
    }
};

export function shortenFilename(filename: string, maxLength: number = 20): string {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return filename; // No extension

    const name = filename.slice(0, lastDot);
    const extension = filename.slice(lastDot);

    if (filename.length <= maxLength) return filename;

    const availableLength = maxLength - extension.length - 3; // 3 for "..."
    const shortenedName = name.slice(0, availableLength);

    return `${shortenedName}...${extension}`;
}

export function isObject(data?: any): boolean {
    return typeof data === 'object';
}

export const grantPermission = (permission: Permissions | Permissions[], mapper: string | any, id?: number) => {
    try {
        if (typeof mapper === 'string') {
            mapper = JSON.parse(mapper);
        }
        const somePermission = Array.isArray(permission) ? permission.find((per) => mapper[per]) : mapper[permission];

        if (isObject(somePermission)) {
            const metadata = (somePermission as PermissionsData).metadata;
            if (metadata) {
                if (metadata.length) {
                    const allAllow = metadata.reduce((allows: number[], meta) => {
                        if (meta.includes('allow')) {
                            const newMeta = meta.toJson<{ allow?: number[] | string[] }>();
                            if (newMeta?.allow) {
                                newMeta.allow.forEach((key) => {
                                    if (typeof key === 'number') {
                                        allows.push(key);
                                    }
                                });
                            }
                        }
                        return allows;
                    }, []);

                    if (allAllow.length && id) {
                        return allAllow.includes(id);
                    }
                }
            }
        }

        return !!somePermission;
    } catch (e) {
        return false;
    }
};

export const generateRandomNumber = (digits: number) => {
    const min = 10 ** (digits - 1);
    const max = 10 ** digits - 1;
    return Math.floor(min + Math.random() * (max - min + 1));
};

export function minutesToHours(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const lang = localStorage.getItem('crm_locale') || 'en';
    let hoursTitle = '';
    let minsTitle = '';
    let and = '';

    if (lang === 'en') {
        hoursTitle = hours === 1 ? 'hour' : 'hours';
        minsTitle = mins === 1 ? 'minute' : 'minutes';
        and = mins > 0 ? ' and ' : '';
    } else {
        hoursTitle = hours === 1 ? 'hora' : 'horas';
        minsTitle = mins === 1 ? 'minuto' : 'minutos';
        and = mins > 0 ? ' y ' : '';
    }

    if (hours > 0) {
        return `${hours} ${hoursTitle}${and}${mins} ${minsTitle}`;
    }

    return `${mins} ${minsTitle}`;
}

export function addSpanToDivs() {
    const classNames = ['.fbw-msg-bot .fbw-msg-body'];
    classNames.forEach((cla) => {
        const elements = document.querySelectorAll(cla);
        elements.forEach((element) => addSpanToChildrens(element));
    });
}

export function addSpanToChildrens(element: Element) {
    let text;
    if (element.innerHTML.includes('<br>')) {
        const textArr = element.innerHTML.split('<br>');
        if (!textArr.some((te) => te.includes('span'))) {
            text = textArr.reduce(
                (acc, val, index) => acc + (val ? `<span class="parr_${index + 1}">${val}</span>` : '<br><br>'),
                ''
            );
            element.innerHTML = text;
        }
    } else {
        text = `<span class="one_mess">${element.textContent}</span>`;
        element.innerHTML = text;
    }
}

export function watchAndReplaceValues(key: string, extra_config: ExtraConfig, infoWatch: InfoWatch, agentName: string) {
    const newKey: ExtraConfigKeys = (key.split('.').pop() || '') as ExtraConfigKeys;
    const { className, mode, index, beforeClassName, indexPlaceholder, bothModes, isNullBefore } = infoWatch[newKey as keyof InfoWatch] || {};
    const divBefore = beforeClassName ? document.querySelectorAll(beforeClassName) : null;
    let isValidEdit = false;

    if(divBefore) {
        isValidEdit = divBefore.length === indexPlaceholder;
    }

    if(!isValidEdit && isNullBefore && divBefore) {
        isValidEdit = divBefore.length === 0;
    }

    if (className) {
        const divs = document.querySelectorAll(className);
        const currentIndex = index ? index - 1 : 0;
        const div = divs[currentIndex] || divs[currentIndex + 1];
        // console.log(div);
        if (div) {
            const val = (extra_config[newKey] as string)?.replace(/\{agentName}/, agentName)
                .replace(/\{name}/, sessionStorage.getItem('name_user') || '');
            if (mode) {
                if ((beforeClassName && isValidEdit) || !beforeClassName) {
                    div.setAttribute(mode, val);
                    if(bothModes) {
                       const span = div.querySelector('span');
                       if(span) span.textContent = val; 
                    }
                }
            } else {
                div.textContent = val;
            }
        }
    }
}

export const sbx360icon = `data:image/png;base64,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`;
